import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import DialogSearch from '@/components/DialogSearch/DialogSearch';
import ModelSeriesBlockedSelector from '@/components/ModelSeriesBlockedSelector/ModelSeriesBlockedSelector';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import DisclaimerCheckbox from '@/components/UI/DisclaimerCheckbox/DisclaimerCheckbox';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import MandatoryLabel from '@/components/UI/MandatoryLabel/MandatoryLabel';
import { dialogDecisionOptions, ERROR, errorTypes, highlightedFieldsInitialState, statusSubCampaign } from '@/helpers/constants';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import { getVersionUrlParam, hideFeedbackSubCR, isAssignInfoReady, isSummary, toLanguageObject } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import useVisibilityMatrix from '@/hooks/useVisibilityMatrix';
import { setHighlightedFields } from '@/redux/HighlightFields/highlightFields.action';
import campaignServices from '@/services/campaignServices';
import { HighlightedFields, SubCampaignInfo, Summary, User } from '@/types';
import { StatusCampaign, StatusSubCampaign, VisibilityMatrixAction, VisibilityMatrixFeature, WorkflowType } from '@/types/Matrix';
import styles from '../SubCampaignHeader.module.scss';

export type ModelSeriesBlockedState = {
  modelSerie: string;
  blocked: null | 'YES' | 'NO';
};

type DecisionFormType = {
  decision: string;
  comment: string;
  blockVehicles: boolean | undefined;
  plantNotAffectedConfirmed: boolean;
};

interface SummaryButtonProps {
  subCRId: string;
  subCRInfo: SubCampaignInfo;
  saveSubCR: (fromShowSummaryBtn?: boolean) => Promise<string | undefined>;
  subCRHasChanges: boolean;
  reloadSubCR: (reason?: string) => void;
  userInfo: User;
  isVANSubCR: boolean;
  summary: Summary;
  setSelectedFields: (fields: HighlightedFields) => void;
}
const NO_MANDATORY_COMMENTS_ROLES = [statusSubCampaign.IN_PLANT_APPROVAL];
const MS_BLOCKED_STATUS = [statusSubCampaign.IN_E3_APPROVAL] as string[];
const DECISION_FORM_FIELDS = {
  DECISION: 'decision',
  COMMENT: 'comment',
} as const;

type DecisionFormField = typeof DECISION_FORM_FIELDS[keyof typeof DECISION_FORM_FIELDS];

const SummaryButton: React.FC<SummaryButtonProps> = (props) => {
  const { subCRId, subCRInfo, saveSubCR, subCRHasChanges, reloadSubCR, userInfo, isVANSubCR, setSelectedFields } = props;
  const [subCRStatus, workflowType] = [subCRInfo?.status, subCRInfo?.workflowType];
  const { t } = useTranslation();
  const { version } = useParams();
  const { goTo } = useGoTo();
  const { addToast } = useToastContext();
  const { isButtonDisabled, asStandIn } = useCRPermissionMatrix(subCRInfo?.status, subCRInfo?.authorization);
  const { canSee } = useVisibilityMatrix();
  const canSeeCRWftp = (action: VisibilityMatrixAction) => canSee({
    feature: VisibilityMatrixFeature.SUB_CR,
    action: action,
    workflow: workflowType as WorkflowType,
    status: subCRStatus as StatusCampaign | StatusSubCampaign, // TODO Missing type on status
  }) || !isButtonDisabled(action as unknown as CR_PERMISSION_ACTIONS, workflowType as WorkflowType);
  const showSubmitSuccess = () => addToast(t('alert.successfullySent'));
  const [isLoading, setIsLoading] = useState(false);
  const [openCampaignManagerDialog, setOpenCampaignManagerDialog] = useState(false);
  const [openDecisionDialog, setOpenDecisionDialog] = useState(false);
  const [decisionForm, setDecisionForm] = useState<DecisionFormType>({ decision: dialogDecisionOptions.APPROVED, comment: '', blockVehicles: undefined, plantNotAffectedConfirmed: false });
  const [doormanWarningDialog, setDoormanWarningDialog] = useState(false);
  const [errorState, setErrorState] = useState({
    decisionHasMandatoryMSBlockedError: false,
    decisionHasMandatoryError: false,
    decisionHasMinLengthError: false,
  });
  const [assignManagerDisabled, setAssignManagerDisabled] = useState(true);
  const [isCheckingAssignable, setIsCheckingAssignable] = useState(true);
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();
  const assignInfoReady = isAssignInfoReady(subCRInfo);

  const isTheAssignedUser = userInfo.userCode === subCRInfo.assignedUser?.code;
  const decisionOptionsByStatus = {
    [statusSubCampaign.IN_ANALYSIS]:
    {
      ...toLanguageObject(dialogDecisionOptions.SEND_TO_APPROVAL, 'buttonsHeader.sendToApproval'),
      ...toLanguageObject(dialogDecisionOptions.CHANGE, 'action.CHANGE'),
    },
    [statusSubCampaign.IN_CHECKING]: {
      ...toLanguageObject(dialogDecisionOptions.APPROVED, 'action.CHECK_SUCCESSFUL'),
      ...toLanguageObject(dialogDecisionOptions.CHANGE, 'action.CHANGE_NEEDED'),
      ...toLanguageObject(dialogDecisionOptions.PLANT_NOT_AFFECTED, 'action.PLANT_NOT_AFFECTED'),
    },
    default: {
      ...toLanguageObject(dialogDecisionOptions.APPROVED, 'action.APPROVED'),
      ...toLanguageObject(dialogDecisionOptions.REJECTED, 'action.REJECTED'),
    },
  };
  const decisionOptions = decisionOptionsByStatus[subCRStatus] || decisionOptionsByStatus.default;
  const summaryButtonsInFullSubCR = hideFeedbackSubCR(subCRInfo, [statusSubCampaign.IN_ANALYSIS]);
  const isVanCoordination = isVANSubCR && subCRInfo.status === statusSubCampaign.IN_VAN_PLANT_COORDINATION;
  const hasSelectFieldsButton = decisionForm.decision === dialogDecisionOptions.CHANGE && !isVANSubCR;

  const advanceNextStatus = async (userCode, comment = '', isCampaignManager = false) => {
    const isPlantApprovalApproved = (MS_BLOCKED_STATUS as string[]).includes(subCRStatus) && decisionForm.decision === 'APPROVED';
    const data = { comment, ...(isPlantApprovalApproved ? { blockVehicles: decisionForm.blockVehicles } : {}) };
    setIsLoading(true);

    if (subCRHasChanges) {
      await saveSubCR();
    }

    if (isCampaignManager) {
      campaignServices.putAssignSubCR(subCRId, userCode)
        .then(() => reloadSubCR(decisionForm.decision))
        .catch((error) => {
          if (error.code === errorTypes.campaign_should_close_by_doorman_status) {
            setDoormanWarningDialog(true);
          } else {
            addToast(error.message, ERROR);
          }
        })
        .finally(() => setIsLoading(false));
    } else {
      campaignServices.advanceNextStatusSubCampaign(subCRId, data, isPlantApprovalApproved)
        .then(() => {
          showSubmitSuccess();
          reloadSubCR(decisionForm.decision);
          goTo(`/sub-campaign/${subCRId}/feedback`, 250);
        })
        .catch((error) => {
          if (error.code === errorTypes.campaign_should_close_by_doorman_status) {
            setDoormanWarningDialog(true);
          } else {
            addToast(error.message, ERROR);
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const rejectDecision = (comment: string = '') => {
    setIsLoading(true);
    campaignServices.rejectSubCampaign(subCRId, {
      comment,
    }).then(() => {
      showSubmitSuccess();
      reloadSubCR(decisionForm.decision);
    }).catch((error) => {
      if (error.code === errorTypes.campaign_should_close_by_doorman_status) {
        setDoormanWarningDialog(true);
      } else {
        addToast(error.message, ERROR);
      }
    }).finally(() => setIsLoading(false));
  };

  const changeDecision = (comment: string = '') => {
    campaignServices.changeDecision(subCRId, {
      comment,
    }, true).then(() => {
      showSubmitSuccess();
      reloadSubCR(decisionForm.decision);
      goTo(`/sub-campaign/${subCRId}/feedback`, 250);
    }).catch((error) => {
      if (error.code === errorTypes.campaign_should_close_by_doorman_status) {
        setDoormanWarningDialog(true);
      } else {
        addToast(error.message, ERROR);
      }
    });
  };

  const plantNotAffected = (comment: string = '') => {
    setIsLoading(true);
    campaignServices.plantNotAffected(subCRId, {
      comment,
    }).then(() => {
      showSubmitSuccess();
      reloadSubCR(decisionForm.decision);
    }).catch((error) => {
      if (error.code === errorTypes.campaign_should_close_by_doorman_status) {
        setDoormanWarningDialog(true);
      } else {
        addToast(error.message, ERROR);
      }
    }).finally(() => setIsLoading(false));
  };

  const validateDecisionComment = (newState: DecisionFormType) => {
    let newErrorState = { ...errorState };
    const rejectCommentNoMandatory = (NO_MANDATORY_COMMENTS_ROLES as string[]).includes(subCRStatus) && !isVANSubCR;

    switch (newState.decision) {
      case dialogDecisionOptions.APPROVED:
        newErrorState = {
          ...errorState,
          decisionHasMandatoryError: false,
          decisionHasMandatoryMSBlockedError: newState.blockVehicles === undefined && (MS_BLOCKED_STATUS as string[]).includes(subCRStatus),
        };
        break;
      case dialogDecisionOptions.SEND_TO_APPROVAL:
        newErrorState = { ...errorState, decisionHasMandatoryError: false };
        break;
      case dialogDecisionOptions.CHANGE:
      case dialogDecisionOptions.REJECTED:
        newErrorState = {
          ...errorState,
          decisionHasMandatoryError: rejectCommentNoMandatory ? false : !newState.comment,
          decisionHasMinLengthError: rejectCommentNoMandatory ? false : !!(newState.comment && newState.comment?.length < 8),
          decisionHasMandatoryMSBlockedError: false,
        };
        break;
      case dialogDecisionOptions.PLANT_NOT_AFFECTED:
        newErrorState = {
          ...errorState,
          decisionHasMandatoryError: false,
          decisionHasMinLengthError: false,
          decisionHasMandatoryMSBlockedError: false,
        };
        break;
      default:
        break;
    }
    setErrorState(newErrorState);

    return newErrorState;
  };

  const confirmDecision = () => {
    const newErrorState = validateDecisionComment(decisionForm);
    if (Object.values(newErrorState).some((error) => error)) return;

    switch (decisionForm.decision) {
      case dialogDecisionOptions.APPROVED:
        advanceNextStatus(null, decisionForm.comment);
        break;
      case dialogDecisionOptions.REJECTED:
        rejectDecision(decisionForm.comment);
        break;
      case dialogDecisionOptions.SEND_TO_APPROVAL:
        advanceNextStatus(null, decisionForm.comment);
        break;
      case dialogDecisionOptions.CHANGE:
        changeDecision(decisionForm.comment);
        break;
      case dialogDecisionOptions.PLANT_NOT_AFFECTED:
        plantNotAffected(decisionForm.comment);
        break;
      default:
        break;
    }

    setOpenDecisionDialog(false);
  };

  const handleDecisionFormChanges = (label: DecisionFormField, value: string) => {
    const newState = {
      ...decisionForm,
      ...(label === DECISION_FORM_FIELDS.DECISION && { plantNotAffectedConfirmed: false }),
      [label]: value,
    };
    setDecisionForm(newState);
    validateDecisionComment(newState);
  };

  const handleClickSendToApproval = () => {
    advanceNextStatus(null);
  };

  const handleClickSelectFields = () => {
    setSelectedFields({
      ...highlightedFieldsInitialState,
      isSelectionMode: true,
      reviewerComment: decisionForm.comment,
    });
    setOpenDecisionDialog(false);
    goTo(`/sub-campaign/${subCRId}${getVersionUrlParam(version)}/description`);
    addToast(t('alert.startSelectFieldsMode'), 'Info');
  };

  const isPlantNotAffected = decisionForm.decision === dialogDecisionOptions.PLANT_NOT_AFFECTED;
  const disabledDecisionSubmit = errorState.decisionHasMandatoryError || errorState.decisionHasMinLengthError
    || errorState.decisionHasMandatoryMSBlockedError
    || (isPlantNotAffected && !decisionForm.plantNotAffectedConfirmed);

  const checkIsAssignable = useApiRequest(campaignServices.checkIsAssignable);

  const handleMSBlockedChange = (blocked: boolean) => {
    const newDecisionForm = {
      ...decisionForm,
      blockVehicles: blocked,
    };
    validateDecisionComment(newDecisionForm);
    setDecisionForm(newDecisionForm);
  };

  const handlePlantNotAffectedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDecisionForm({ ...decisionForm, plantNotAffectedConfirmed: event.target.checked });
  };

  useEffect(() => {
    setGlobalLoading(!assignInfoReady || isCheckingAssignable);

    return () => setGlobalLoading(false);
  }, [assignInfoReady, isCheckingAssignable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!assignInfoReady) return;
    if (canSeeCRWftp(VisibilityMatrixAction.ASSIGN_MANAGER)) {
      setIsCheckingAssignable(true);
      checkIsAssignable(subCRId)
        .then((isAssignable) => setAssignManagerDisabled((isTheAssignedUser ? false : subCRInfo?.assignedUser?.code !== '') || !isAssignable))
        .catch((error) => { if (error.name !== 'AbortError') addToast(error.message, ERROR); })
        .finally(() => setIsCheckingAssignable(false));
    } else {
      setIsCheckingAssignable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignInfoReady, subCRInfo.assignedUser?.code]);

  return (
    <>
      {((summaryButtonsInFullSubCR && !isSummary()) ? canSeeCRWftp(VisibilityMatrixAction.ASSIGN_MANAGER) : (!summaryButtonsInFullSubCR && canSeeCRWftp(VisibilityMatrixAction.ASSIGN_MANAGER) && isSummary())) && (
        <>
          {!isTheAssignedUser && (
            <Button
              id="buttonAssignToMeManager"
              handleClick={() => advanceNextStatus(userInfo.userCode, '', true)}
              buttonStyle="SecondaryRoundButton"
              btnText={t('campaignRequests.assignMe')}
              disabled={isLoading || assignManagerDisabled}
              asStandIn={asStandIn}
            />
          )}
          <Button
            id="buttonAssignManager"
            handleClick={() => setOpenCampaignManagerDialog(true)}
            buttonStyle="SecondaryRoundButton"
            btnText={t('campaignRequests.assignManager')}
            disabled={isLoading || assignManagerDisabled}
            asStandIn={asStandIn}
          />
        </>
      )}

      {(canSeeCRWftp(VisibilityMatrixAction.MAKE_DECISION_SUB_CR) && isSummary()) // subCR status: in e3 validation: he can approve or reject the subcr
        ? (
          <Button
            id="buttonMakeDecision"
            handleClick={() => setOpenDecisionDialog(true)}
            buttonStyle="RoundButton"
            btnText={t('buttonsHeader.makeDecision')}
            disabled={isLoading || isButtonDisabled(CR_PERMISSION_ACTIONS.MAKE_DECISION_SUB_CR, workflowType)}
            asStandIn={asStandIn}
          />
        )
        : ((canSeeCRWftp(VisibilityMatrixAction.SEND_TO_APPROVAL_SUB_CR) && isSummary()) || isVanCoordination) && (
          <Button
            id="buttonSubmit"
            handleClick={handleClickSendToApproval}
            buttonStyle="RoundButton"
            btnText={t('buttonsHeader.sendToApproval')}
            disabled={isLoading || isButtonDisabled(CR_PERMISSION_ACTIONS.SEND_TO_APPROVAL_SUB_CR, workflowType)}
            asStandIn={asStandIn}
          />
        )}
      {canSeeCRWftp(VisibilityMatrixAction.COMPLETE_SUB_CR) && !isVanCoordination && (
        <Button
          id="buttonCompleteSubCR"
          handleClick={() => advanceNextStatus(null)}
          buttonStyle="RoundButton"
          btnText={t('buttonsHeader.completeSubCR')}
          disabled={isLoading || isButtonDisabled(CR_PERMISSION_ACTIONS.COMPLETE_SUB_CR, workflowType)}
          asStandIn={asStandIn}
        />
      )}
      {openCampaignManagerDialog && (
        <DialogSearch
          plant={subCRInfo?.plant}
          title={t('campaignRequests.assignManager')}
          close={() => setOpenCampaignManagerDialog(false)}
          updateOptionSelected={(managerSelected) => advanceNextStatus(managerSelected, '', true)}
          assignedUser={subCRInfo?.assignedUser?.code}
        />
      )}
      {openDecisionDialog && (
        <ConfirmDialog
          title={t('decisionDialog.title')}
          confirm={confirmDecision}
          reject={() => setOpenDecisionDialog(false)}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.send')}
          fullWidth
          maxWidth="md"
          confirmDisabled={disabledDecisionSubmit}
          {...hasSelectFieldsButton && {
            customButton: {
              text: t('dialog.selectFields'),
              onClick: handleClickSelectFields,
              disabled: disabledDecisionSubmit,
            },
          }}
        >
          <div className={styles.DecisionContainer}>
            <div className={styles.TwoColumns}>
              <Dropdown
                id="decision"
                label={t('decisionDialog.decisionLabel')}
                options={decisionOptions}
                value={decisionForm.decision}
                handleChange={(event) => { handleDecisionFormChanges(DECISION_FORM_FIELDS.DECISION, event.target.value); }}
              />
            </div>
            {(MS_BLOCKED_STATUS.includes(subCRInfo.status) && decisionForm.decision === dialogDecisionOptions.APPROVED) && (
              <>
                <p className={styles.MSBlockedTitle}>{t('buttonsHeader.msBlockedSectionTitle')}<MandatoryLabel /></p>
                <div className={styles.TwoColumns}>
                  <ModelSeriesBlockedSelector
                    onChange={handleMSBlockedChange}
                    hasError={errorState.decisionHasMandatoryMSBlockedError}
                    blockVehicles={decisionForm.blockVehicles}
                  />
                  {errorState.decisionHasMandatoryMSBlockedError && <div className={styles.MSBlockedWarning}>{t('validations.required')}</div>}
                  <br />
                </div>
              </>
            )}
            {isPlantNotAffected && (
              <div className={styles.OneColumns}>
                <DisclaimerCheckbox
                  id="plant-not-affected-confirmation"
                  containerStyle={styles.plantNotAffectedCheckbox}
                  required
                  checked={decisionForm.plantNotAffectedConfirmed}
                  onChange={handlePlantNotAffectedChange}
                  message="decisionDialog.plantNotAffectedConfirmation"
                />
              </div>
            )}
            <div className={styles.OneColumns}>
              <InputText
                invalid={errorState.decisionHasMandatoryError || errorState.decisionHasMinLengthError}
                isMandatory={(NO_MANDATORY_COMMENTS_ROLES as string[]).includes(subCRStatus)
                  ? false
                  : ['REJECT', dialogDecisionOptions.CHANGE]
                    .includes(decisionForm.decision)}
                multiline
                maxLength={1024}
                rows={5}
                id="decision-comment"
                label={t('comment')}
                value={decisionForm.comment}
                handleChange={(event) => { handleDecisionFormChanges(DECISION_FORM_FIELDS.COMMENT, event.target.value); }}
                helperText={[
                  ...((errorState.decisionHasMandatoryError) ? [t('validations.required')] : []),
                  ...((errorState.decisionHasMinLengthError) ? [t('validations.commentsMinLength')] : []),
                ]}
              />
            </div>
          </div>
        </ConfirmDialog>
      )}
      {doormanWarningDialog && (
        <ConfirmDialog
          confirm={() => {
            setDoormanWarningDialog(false);
            reloadSubCR();
          }}
          confirmBtnText={t('dialog.ok')}
          message={t('dialog.doormanMessage')}
        />
      )}
    </>
  );
};

const actionCreators = {
  setSelectedFields: setHighlightedFields,
};

export default connect(null, actionCreators)(SummaryButton);
